import React from 'react';
import { ColumnsType } from 'antd/es/table';
import { Tag, Image, Skeleton, Tooltip } from 'antd';
import { ShopifyMedia } from '../../../store/shopifyMediaSlice';
import { GenerationStatus, SyncStatus } from '../../../constants/status';
import { statusColorGeneration, statusColorSync } from '../../../constants/statusColors';

interface ColumnsProps {
    getColumnSearchProps: (dataIndex: string, customRender?: any) => any;
    handleShopifyProductImageModalOpen: (shopifyMedia: ShopifyMedia) => void;
}

export const getColumns = ({
    getColumnSearchProps,
    handleShopifyProductImageModalOpen,
}: ColumnsProps): ColumnsType<ShopifyMedia> => [
        {
            title: 'Image',
            dataIndex: ['preview', 'image', 'url'],
            key: "url",
            width: 60,
            render: (src) => (
                <div style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                    {src ? (
                        <Image
                            width={40}
                            height={40}
                            src={src}
                            fallback="https://via.placeholder.com/40" // Placeholder image if loading fails
                            style={{ objectFit: 'cover' }}
                        />
                    ) : (
                        <Skeleton.Avatar active size="small" shape="square" />
                    )}
                </div>
            ),
            fixed: 'left'
        },
        {
            title: 'Id',
            dataIndex: 'shopifyId',
            key: 'shopifyId',
            width: 100,
            render: (shopifyId, shopifyMedia) => <a onClick={() => handleShopifyProductImageModalOpen(shopifyMedia)}>{shopifyId}</a>,
            fixed: 'left',
            sorter: (a, b) => a.shopifyId.localeCompare(b.shopifyId)
        },
        {
            title: "Product Title",
            dataIndex: "productTitle",
            key: "productTitle",
            width: 200,
            render: (productTitle) => productTitle,
            sorter: (a, b) => a.productTitle.localeCompare(b.productTitle),
            fixed: 'left',
            ...getColumnSearchProps(
                'title',
                (highlightedText: string, shopifyProductImage: ShopifyMedia) => (
                    <Tooltip title={shopifyProductImage.productTitle} >
                        {highlightedText}
                    </Tooltip>
                )
            )
        },
        {
            title: 'Has Alt',
            dataIndex: 'alt',
            key: 'alt',
            render: (alt, record) => (
                <Tag key={record.id + '_has_alt'}>{alt ? "Yes" : "No"}</Tag>
            ),
            filters: [{
                text: 'Yes',
                value: true,
            }, {
                text: 'No',
                value: false,
            }],
            onFilter: (value, record) => !!record.alt === value,
            width: 120
        },
        {
            title: 'Sync Status',
            dataIndex: 'syncStatus',
            key: 'syncStatus',
            sorter: (a, b) => (a.syncStatus || "").localeCompare(b.syncStatus || ""), // Alphabetical sorting
            filters: Object.values(SyncStatus).map((syncStatus) => ({
                text: syncStatus,
                value: syncStatus,
            })),
            onFilter: (value, record) => record.syncStatus === value,
            render: (syncStatus: SyncStatus | null | undefined) => {
                if (!syncStatus) {
                    return <Tag>Unknown</Tag>
                }
                return (
                    <Tag color={statusColorSync[syncStatus]}>
                        {syncStatus.charAt(0).toUpperCase() + syncStatus.slice(1)} {/* Capitalize */}
                    </Tag>
                )
            },
        },
        {
            title: 'Generation Status',
            dataIndex: 'generationStatus',
            key: 'generationStatus',
            sorter: (a, b) => (a.generationStatus || "").localeCompare(b.generationStatus || ""), // Alphabetical sorting
            filters: Object.values(GenerationStatus).map((generationStatus) => ({
                text: generationStatus,
                value: generationStatus,
            })),
            onFilter: (value, record) => record.generationStatus === value,
            render: (status: GenerationStatus) => (
                <Tag color={statusColorGeneration[status]} >
                    {status}
                </Tag>
            )
        }
    ];
